import { debounce } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { sectionRenderer } from '@theme/section-renderer';

const configEl = document.getElementById('ak-search-config');
const root = document.getElementById('search-modal');

if (root && configEl) {
  let cfg = null;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch (error) {
    console.warn('[akmas-search] config parse failed', error);
  }
  if (cfg) mount(root, cfg);
}

function mount(rootEl, cfg) {
  const dialogEl = rootEl.querySelector('dialog');
  const panel = rootEl.querySelector('[data-ak-search-panel]');
  const input = rootEl.querySelector('[data-ak-search-input]');
  const form = rootEl.querySelector('[data-ak-search-form]');
  const listEl = rootEl.querySelector('[data-ak-search-list]');
  const seeAll = rootEl.querySelector('[data-ak-search-seeall]');
  const statusEl = rootEl.querySelector('[data-ak-search-status]');
  const clearBtn = rootEl.querySelector('[data-ak-search-clear]');
  const recentClear = rootEl.querySelector('[data-ak-recent-clear]');
  const blocks = {
    recent: rootEl.querySelector('[data-ak-search-recent]'),
    recentChips: rootEl.querySelector('[data-ak-recent-chips]'),
    noresults: rootEl.querySelector('[data-ak-search-noresults]'),
    noresultsText: rootEl.querySelector('[data-ak-noresults]'),
    results: rootEl.querySelector('[data-ak-search-results-block]'),
    loading: rootEl.querySelector('[data-ak-search-loading]'),
    trending: rootEl.querySelector('[data-ak-search-trending]'),
    notes: rootEl.querySelector('[data-ak-search-notes]'),
    offers: rootEl.querySelector('[data-ak-search-offers]'),
    best: rootEl.querySelector('[data-ak-search-best]'),
  };

  if (!dialogEl || !panel || !input || !form || !listEl || !statusEl) return;

  const i18n = cfg.i18n || {};
  const state = { seq: 0, q: '', lastTerm: '', lastNoRes: '', active: -1 };
  let indexItems = [];
  let indexPromise = null;
  let indexLoading = false;

  const aliases = new Map(
    (cfg.aliases || []).map((entry) => [norm(entry.from), norm(entry.to)])
  );

  const STOPWORDS = new Set(['a', 'an', 'the', 'for', 'of', 'and', 'or', 'in', 'on', 'to', 'is']);
  const PRICE_PREFIX = /\b(?:under|below|less than|upto|up to|within)\s+(\d{2,6})\b/;
  const PRICE_SUFFIX = /\b(\d{2,6})\s+(?:or less|or below|max|maximum)\b/;
  /* Neutral perfume-bottle placeholder shown when a result image is missing
     or fails to load, never an empty grey box. */
  const BOTTLE_FALLBACK =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Crect width='64' height='64' rx='10' fill='%23FFF3F1'/%3E%3Crect x='26' y='10' width='12' height='8' rx='2' fill='%23E4C9C4'/%3E%3Cpath d='M22 18h20l3 8v28a4 4 0 0 1-4 4H23a4 4 0 0 1-4-4V26z' fill='%23fff' stroke='%23E4C9C4' stroke-width='2'/%3E%3Crect x='25' y='32' width='14' height='12' rx='2' fill='%23F5D7D2'/%3E%3C/svg%3E";

  /* ------------------------------------------------------------------
     Recent searches (localStorage)
     ------------------------------------------------------------------ */
  function recentGet() {
    try {
      const value = JSON.parse(localStorage.getItem(cfg.recentKey) || '[]');
      return Array.isArray(value) ? value.filter((t) => typeof t === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  function commitRecent(term) {
    const clean = String(term || '').trim();
    if (!clean) return;
    const list = recentGet().filter((t) => t.toLowerCase() !== clean.toLowerCase());
    list.unshift(clean);
    try {
      localStorage.setItem(cfg.recentKey, JSON.stringify(list.slice(0, cfg.recentMax || 5)));
    } catch (error) {
      /* storage full or unavailable */
    }
  }

  function renderRecent() {
    if (!blocks.recentChips) return;
    const items = recentGet();
    blocks.recentChips.innerHTML = items
      .map(
        (term) =>
          `<button type="button" class="ak-chip" data-ak-term="${escAttr(term)}">${esc(term)}</button>`
      )
      .join('');
    if (blocks.recent) blocks.recent.hidden = items.length === 0 || panel.dataset.state !== 'empty';
  }

  /* ------------------------------------------------------------------
     Analytics, pushes to dataLayer when a tag manager is present and
     always dispatches a window event so the theme can hook in later.
     ------------------------------------------------------------------ */
  function track(name, payload) {
    try {
      const detail = Object.assign({ event: name }, payload || {});
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
      window.dispatchEvent(new CustomEvent('akmas:analytics', { detail }));
    } catch (error) {
      /* never break search because analytics failed */
    }
  }

  /* ------------------------------------------------------------------
     Product index (Section Rendering API, cached in localStorage)
     ------------------------------------------------------------------ */
  function loadCachedIndex() {
    try {
      const raw = localStorage.getItem(cfg.indexKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !Array.isArray(data.items) || !data.items.length) return null;
      if (Date.now() - (data.ts || 0) > (cfg.indexTtl || 21600000)) return null;
      return data.items;
    } catch (error) {
      return null;
    }
  }

  async function fetchIndexPage(page) {
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', cfg.indexSectionId);
    url.searchParams.set('page', String(page));
    const html = await sectionRenderer.getSectionHTML(cfg.indexSectionId, false, url);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const payload = doc.querySelector('[data-ak-search-index]');
    if (!payload) throw new Error('index payload missing');
    return JSON.parse(payload.textContent);
  }

  async function loadIndex() {
    if (indexPromise) return indexPromise;
    const cached = loadCachedIndex();
    if (cached) indexItems = cached.filter((item) => !isGift(item));

    indexPromise = (async () => {
      indexLoading = true;
      try {
        const first = await fetchIndexPage(1);
        const items = first.items.slice();
        const queue = [];
        for (let page = 2; page <= (first.pages || 1); page++) queue.push(page);

        const workers = [];
        for (let w = 0; w < 3 && queue.length; w++) {
          workers.push(
            (async () => {
              while (queue.length) {
                const page = queue.shift();
                try {
                  const data = await fetchIndexPage(page);
                  items.push(...data.items);
                } catch (error) {
                  console.warn('[akmas-search] index page failed', page, error);
                }
              }
            })()
          );
        }
        await Promise.all(workers);

        if (items.length) {
          indexItems = items.filter((item) => !isGift(item));
          try {
            localStorage.setItem(cfg.indexKey, JSON.stringify({ ts: Date.now(), items: indexItems }));
          } catch (error) {
            /* quota, index stays in memory only */
          }
        }
      } catch (error) {
        console.warn('[akmas-search] index load failed', error);
        indexPromise = null;
      } finally {
        indexLoading = false;
        if (dialogEl && dialogEl.open && norm(input.value).length >= 2) void runSearch();
      }
    })();

    return indexPromise;
  }

  function ensureIndex() {
    if (indexPromise || indexItems.length) return;
    const start = () => void loadIndex();
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 1500 });
    else setTimeout(start, 400);
  }

  /* ------------------------------------------------------------------
     Predictive suggest (collections + authoritative typo matching)
     ------------------------------------------------------------------ */
  let suggestCtl = null;

  async function fetchSuggest(query) {
    if (suggestCtl) suggestCtl.abort();
    suggestCtl = new AbortController();
    const url = new URL(cfg.suggestUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const response = await fetch(url, { signal: suggestCtl.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const data = await response.json();
      return data && data.resources ? data.resources.results : null;
    } catch (error) {
      if (error && error.name === 'AbortError') throw error;
      return null;
    }
  }

  /* ------------------------------------------------------------------
     Text utilities
     ------------------------------------------------------------------ */
  function norm(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function lev(a, b, max) {
    if (a === b) return 0;
    const la = a.length;
    const lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    let prev = new Array(lb + 1);
    let curr = new Array(lb + 1);
    for (let j = 0; j <= lb; j++) prev[j] = j;
    for (let i = 1; i <= la; i++) {
      curr[0] = i;
      let rowMin = i;
      const code = a.charCodeAt(i - 1);
      for (let j = 1; j <= lb; j++) {
        const cost = code === b.charCodeAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
        if (curr[j] < rowMin) rowMin = curr[j];
      }
      if (rowMin > max) return max + 1;
      const tmp = prev;
      prev = curr;
      curr = tmp;
    }
    return prev[lb];
  }

  function isSubsequence(needle, haystack) {
    let i = 0;
    for (let j = 0; j < haystack.length && i < needle.length; j++) {
      if (needle[i] === haystack[j]) i++;
    }
    return i === needle.length;
  }

  function splitTitle(title) {
    const lower = title.toLowerCase();
    const idx = lower.indexOf('inspired by');
    if (idx > 0) {
      const name = title.slice(0, idx).trim();
      if (name) return { name, inspired: title.slice(idx + 11).trim() };
    }
    return { name: title, inspired: '' };
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(value) {
    return esc(value).replace(/'/g, '&#39;');
  }

  function escRe(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function highlight(text, terms) {
    const safe = esc(text);
    const words = terms.filter((term) => term.length >= 2).map(escRe);
    if (!words.length) return safe;
    try {
      return safe.replace(new RegExp(`(${words.join('|')})`, 'gi'), '<mark>$1</mark>');
    } catch (error) {
      return safe;
    }
  }

  function money(cents) {
    const formatted = formatMoney(cents, cfg.moneyFormat, cfg.currency);
    return formatted.replace(/\.00$/, '');
  }

  function parseQuery(raw) {
    const full = norm(raw);
    let cap = 0;
    let text = full;
    let match = text.match(PRICE_PREFIX) || text.match(PRICE_SUFFIX);
    if (match) {
      cap = parseInt(match[1], 10) * 100;
      text = (text.slice(0, match.index) + ' ' + text.slice(match.index + match[0].length)).trim();
    }
    const tokens = text.split(/\s+/).filter(Boolean);
    const required = [];
    for (const token of tokens) {
      if (STOPWORDS.has(token)) continue;
      const replacement = aliases.get(token);
      if (replacement) required.push(...replacement.split(/\s+/).filter(Boolean));
      else required.push(token);
    }
    return { full, text, tokens, required: [...new Set(required)], cap, phrase: text };
  }

  /* ------------------------------------------------------------------
     Scoring
     ------------------------------------------------------------------ */
  function prepare(item) {
    if (item._p) return item._p;
    const notes = Array.isArray(item.nt) ? item.nt : item.nt ? String(item.nt).split(',') : [];
    const parts = [
      [item.n, 4],
      [item.i, 3.5],
      [item.t, 3],
      [item.gd, 3],
      [notes.join(' '), 2.5],
      [item.sf, 2.5],
      [(item.tg || []).join(' '), 2],
      [item.ty, 1.5],
      [item.h, 1.5],
      [item.d, 1],
    ]
      .map(([text, weight]) => {
        const normalized = norm(text);
        return { text: normalized, toks: normalized ? normalized.split(' ') : [], w: weight };
      })
      .filter((part) => part.text);

    const phrase = norm([item.n, item.i, item.t, item.h].filter(Boolean).join(' '));
    item._p = { parts, phrase };
    return item._p;
  }

  function scoreTerm(prep, term) {
    const tl = term.length;
    const tol = tl >= 7 ? 2 : tl >= 4 ? 1 : 0;
    let best = 0;

    for (const part of prep.parts) {
      let hit = 0;
      for (let i = 0; i < part.toks.length; i++) {
        const tok = part.toks[i];
        if (tok === term) {
          hit = part.w;
          break;
        }
        if (tok.startsWith(term)) {
          if (hit < part.w * 0.8) hit = part.w * 0.8;
        } else if (tok.includes(term)) {
          if (hit < part.w * 0.55) hit = part.w * 0.55;
        } else if (tol > 0 && Math.abs(tok.length - tl) <= tol && lev(term, tok, tol) <= tol) {
          if (hit < part.w * 0.5) hit = part.w * 0.5;
        }
      }
      if (hit === 0 && tl >= 5 && isSubsequence(term, part.text)) hit = part.w * 0.25;
      if (hit > best) best = hit;
      if (best >= 4) break;
    }

    return best;
  }

  function searchIndex(items, parsed) {
    if (!parsed.required.length && !parsed.cap) return [];
    const scored = [];

    for (const item of items) {
      if (parsed.cap && item.pr > parsed.cap) continue;
      if (!parsed.required.length) {
        scored.push([0, item]);
        continue;
      }
      const prep = prepare(item);
      let score = 0;
      let matched = true;
      for (const term of parsed.required) {
        const termScore = scoreTerm(prep, term);
        if (!termScore) {
          matched = false;
          break;
        }
        score += termScore;
      }
      if (!matched) continue;
      if (parsed.phrase && prep.phrase.includes(parsed.phrase)) score += 8;
      if (item.rc) score += Math.min(item.rc, 50) / 100;
      scored.push([score, item]);
    }

    scored.sort(
      (a, b) => b[0] - a[0] || (b[1].rc || 0) - (a[1].rc || 0) || a[1].pr - b[1].pr
    );
    return scored.map((entry) => entry[1]);
  }

  function fromSuggest(product) {
    const title = product.title || '';
    const { name, inspired } = splitTitle(title);
    const price = Math.round((parseFloat(String(product.price || '').replace(/,/g, '')) || 0) * 100);
    const compare = Math.round(
      (parseFloat(String(product.compare_at_price || '').replace(/,/g, '')) || 0) * 100
    );
    const baseline = compare > price ? compare : 0;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstVariant = variants[0] || null;
    const tags = product.tags || [];
    const genderTag = tags.find(
      (tag) => ['men', 'women', 'unisex'].includes(String(tag).toLowerCase().trim())
    );
    /* Predictive search returns `featured_image` as an object and `image` as a
       URL string. There is no `featured_image_url` key, which is why every row
       used to fall back to the bottle placeholder. */
    const featured = product.featured_image || null;
    const img = (featured && featured.url) || product.image || null;
    return {
      id: product.id,
      h: product.handle || '',
      u: product.url || '/',
      t: title,
      n: name,
      i: inspired,
      ty: product.type || '',
      tg: tags,
      d: '',
      nt: [],
      sf: null,
      gd: genderTag ? genderTag.charAt(0).toUpperCase() + genderTag.slice(1).toLowerCase() : null,
      r: null,
      rc: null,
      pr: price,
      bl: baseline,
      sp: baseline > price ? Math.round(((baseline - price) * 100) / baseline) : 0,
      av: product.available !== false,
      vi: firstVariant ? firstVariant.id : 0,
      vc: variants.length || 1,
      va: variants.length
        ? variants.some((variant) => variant.available !== false)
        : product.available !== false,
      img,
      is: cdnSrcset(img),
    };
  }

  /* Shopify's predictive-search image URL is a CDN link, so a 1x/2x srcset can
     be built from it by appending a width parameter. */
  function cdnSrcset(url) {
    const value = String(url || '');
    if (!/\/cdn\/shop\//.test(value)) return '';
    const joiner = value.indexOf('?') >= 0 ? '&' : '?';
    return `${value}${joiner}width=128 1x, ${value}${joiner}width=256 2x`;
  }

  /* Free gift soaps (and their duplicate products) are not shoppable search
     results, so they never enter the results list. Accepts both the index
     shape (ty/tg) and the predictive-search shape (type/tags). */
  function isGift(product) {
    const type = String(product.type || product.ty || '').toLowerCase().trim();
    if (type === 'soap') return true;
    const tags = (product.tags || product.tg || []).map((tag) => String(tag).toLowerCase().trim());
    return tags.indexOf('kwikcart-freebie') >= 0 || tags.indexOf('soap') >= 0;
  }

  function mergeProducts(local, parsed, suggest) {
    const out = local.slice();
    const seen = new Set(out.map((item) => item.id));
    for (const product of (suggest && suggest.products) || []) {
      if (seen.has(product.id)) continue;
      if (isGift(product)) continue;
      const item = fromSuggest(product);
      if (parsed.cap && item.pr > parsed.cap) continue;
      seen.add(item.id);
      out.push(item);
    }
    return out;
  }

  /* ------------------------------------------------------------------
     Rendering
     ------------------------------------------------------------------ */
  function stars(rating) {
    const rounded = Math.round(Number(rating));
    return '★'.repeat(rounded) + '☆'.repeat(5 - Math.min(rounded, 5));
  }

  function groupHead(label) {
    return `<div class="ak-search__head"><h3 class="ak-search__heading">${esc(label)}</h3></div>`;
  }

  function rowHTML(item, terms, id) {
    const price = item.pr > 0 ? `<span class="ak-price">${money(item.pr)}</span>` : '';
    const compare =
      item.bl && item.bl > item.pr ? `<span class="ak-price ak-price--compare">${money(item.bl)}</span>` : '';
    const save = item.sp ? `<span class="ak-card__save">-${item.sp}%</span>` : '';

    const rating =
      item.r != null && item.rc != null
        ? `<span class="ak-row__rating"><span class="ak-card__stars" aria-hidden="true">${stars(
            item.r
          )}</span><span class="ak-card__rating-value">${Number(item.r).toFixed(1)}</span>` +
          `<span class="ak-card__rating-count">(${item.rc})</span>` +
          `<span class="ak-sr-only">Rated ${Number(item.r).toFixed(1)} out of 5 from ${item.rc} reviews</span></span>`
        : '';

    const alt = item.n || item.t || '';
    const srcset = item.is || cdnSrcset(item.img);
    const media = item.img
      ? `<img class="ak-row__img" src="${escAttr(item.img)}"${
          srcset ? ` srcset="${escAttr(srcset)}" sizes="64px"` : ''
        } alt="${escAttr(alt)}" width="128" height="128" loading="lazy" decoding="async" onerror="this.onerror=null;this.src='${BOTTLE_FALLBACK}'">`
      : `<img class="ak-row__img" src="${BOTTLE_FALLBACK}" alt="" width="128" height="128" aria-hidden="true">`;

    const cta =
      item.vc > 1 || !item.vi
        ? `<a class="ak-btn ak-btn--primary ak-row__add" href="${escAttr(item.u)}">${esc(
            i18n.choose || 'Choose options'
          )}</a>`
        : `<button type="button" class="ak-btn ak-btn--primary ak-row__add" data-ak-add="${escAttr(
            String(item.vi)
          )}" data-ak-add-label="${escAttr(i18n.add || 'Add')}" aria-label="${escAttr(
            (i18n.add || 'Add') + ' ' + item.n
          )}" ${item.va ? '' : 'disabled'}>${esc(
            item.va ? i18n.add || 'Add' : i18n.soldOut || 'Sold out'
          )}</button>`;

    return (
      `<div class="ak-row" role="option" id="${id}" aria-selected="false" ` +
      `data-type="product" data-id="${escAttr(String(item.id))}" data-handle="${escAttr(item.h)}" ` +
      `data-url="${escAttr(item.u)}">` +
      `<span class="ak-row__media">${media}</span>` +
      `<span class="ak-row__body">` +
      `<span class="ak-row__name">${highlight(item.n, terms)}</span>` +
      (item.i ? `<span class="ak-row__inspired">Inspired by ${highlight(item.i, terms)}</span>` : '') +
      rating +
      `</span>` +
      `<span class="ak-row__foot">` +
      `<span class="ak-row__price">${price}${compare}${save}</span>` +
      cta +
      `</span></div>`
    );
  }

  function collectionRowHTML(collection, terms, id) {
    const featured = collection.featured_image || null;
    const img = (featured && featured.url) || collection.image || null;
    const srcset = cdnSrcset(img);
    const alt = collection.title || '';
    const media = img
      ? `<img class="ak-row__img" src="${escAttr(img)}"${
          srcset ? ` srcset="${escAttr(srcset)}" sizes="64px"` : ''
        } alt="${escAttr(alt)}" width="128" height="128" loading="lazy" decoding="async" onerror="this.onerror=null;this.src='${BOTTLE_FALLBACK}'">`
      : `<img class="ak-row__img" src="${BOTTLE_FALLBACK}" alt="" width="128" height="128" aria-hidden="true">`;
    return (
      `<a class="ak-row" role="option" id="${id}" aria-selected="false" ` +
      `data-type="collection" data-handle="${escAttr(collection.handle || '')}" ` +
      `href="${escAttr(collection.url || '#')}">` +
      `<span class="ak-row__media">${media}</span>` +
      `<span class="ak-row__body">` +
      `<span class="ak-row__name">${highlight(collection.title || '', terms)}</span>` +
      `<span class="ak-row__caption">${esc(i18n.collections || 'Collections')}</span>` +
      `</span>` +
      `<span class="ak-row__chev" aria-hidden="true">&rarr;</span>` +
      `</a>`
    );
  }

  function highlightTerms(parsed) {
    return [...new Set([...parsed.tokens, ...parsed.required])];
  }

  function searchHref(query) {
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('type', 'product');
    return `${cfg.searchUrl}?${params.toString()}`;
  }

  function clearList() {
    listEl.innerHTML = '';
    state.active = -1;
    input.removeAttribute('aria-activedescendant');
  }

  function setState(name) {
    panel.dataset.state = name;
    const empty = name === 'empty';
    const restState = empty || name === 'noresults';
    if (blocks.recent) blocks.recent.hidden = !(empty && recentGet().length > 0);
    if (blocks.noresults) blocks.noresults.hidden = name !== 'noresults';
    if (blocks.results) blocks.results.hidden = name !== 'results';
    if (blocks.loading) blocks.loading.hidden = name !== 'loading';
    if (blocks.trending) blocks.trending.hidden = !restState;
    if (blocks.notes) blocks.notes.hidden = !empty;
    if (blocks.offers) blocks.offers.hidden = !empty;
    if (blocks.best) blocks.best.hidden = !restState;
    if (seeAll) seeAll.hidden = name !== 'results';
    input.setAttribute('aria-expanded', name === 'results' ? 'true' : 'false');
  }

  function setStatus(text) {
    statusEl.textContent = text || '';
  }

  function renderEmpty() {
    clearList();
    setState('empty');
    renderRecent();
    setStatus('');
    if (seeAll) seeAll.hidden = true;
  }

  function renderNoResults(query) {
    clearList();
    setState('noresults');
    if (blocks.noresultsText) {
      blocks.noresultsText.textContent = (i18n.noResults || 'No results found for "{query}".').replace(
        '{query}',
        query
      );
    }
    setStatus((i18n.noResultsSr || 'No results found for "{query}".').replace('{query}', query));
    if (state.lastNoRes !== query) {
      state.lastNoRes = query;
      track('akmas_search_no_result', { term: query });
    }
  }

  function renderResults(query, parsed, products, suggest) {
    const terms = highlightTerms(parsed);
    const top = products.slice(0, 8);
    const collections = ((suggest && suggest.collections) || []).slice(0, 4);

    if (!top.length && !collections.length) {
      if (indexLoading) setState('loading');
      else renderNoResults(query);
      return;
    }

    let html = '';
    let counter = 0;
    if (top.length) {
      html += groupHead(i18n.products || 'Products');
      for (const item of top) html += rowHTML(item, terms, `ak-search-opt-${counter++}`);
    }
    if (collections.length) {
      html += groupHead(i18n.collections || 'Collections');
      for (const collection of collections) {
        html += collectionRowHTML(collection, terms, `ak-search-opt-${counter++}`);
      }
    }

    listEl.innerHTML = html;
    state.active = -1;
    setState('results');
    if (seeAll) {
      seeAll.href = searchHref(query);
      seeAll.textContent = (i18n.seeAllFor || 'See all results for "{query}"').replace(
        '{query}',
        query
      );
    }
    setStatus(
      (i18n.resultsCount || '{count} search results found for "{query}".')
        .replace('{count}', String(top.length + collections.length))
        .replace('{query}', query)
    );
    if (state.lastTerm !== query) {
      state.lastTerm = query;
      track('akmas_search_term', {
        term: query,
        results: top.length,
        collections: collections.length,
      });
    }
  }

  /* ------------------------------------------------------------------
     Search flow
     ------------------------------------------------------------------ */
  async function runSearch() {
    const query = input.value.trim();
    state.q = query;
    if (norm(query).length < 2) {
      state.seq++;
      renderEmpty();
      return;
    }

    const seq = ++state.seq;
    const parsed = parseQuery(query);
    const local = searchIndex(indexItems, parsed);

    let suggestPending = true;
    const suggestPromise = fetchSuggest(query)
      .then((result) => {
        suggestPending = false;
        return result;
      })
      .catch(() => {
        suggestPending = false;
        return null;
      });

    if (local.length) renderResults(query, parsed, local, null);
    else if (indexLoading || suggestPending) setState('loading');
    else renderNoResults(query);

    const suggest = await suggestPromise;
    if (seq !== state.seq) return;

    const merged = mergeProducts(local, parsed, suggest);
    renderResults(query, parsed, merged, suggest);
  }

  const runSearchDebounced = debounce(() => void runSearch(), 200);

  /* ------------------------------------------------------------------
     Keyboard (combobox) support
     ------------------------------------------------------------------ */
  function optionList() {
    return Array.from(listEl.querySelectorAll('[role="option"]'));
  }

  function setActive(index) {
    const options = optionList();
    if (!options.length) {
      state.active = -1;
      input.removeAttribute('aria-activedescendant');
      return;
    }
    const previous = state.active >= 0 ? options[state.active] : null;
    if (previous) previous.setAttribute('aria-selected', 'false');
    state.active = ((index % options.length) + options.length) % options.length;
    const active = options[state.active];
    active.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  function activateOption(option) {
    const term = state.q;
    commitRecent(term);
    track('akmas_search_result_clicked', {
      term,
      type: option.dataset.type || null,
      id: option.dataset.id || null,
      handle: option.dataset.handle || null,
    });
    if (option.dataset.url) {
      window.location.href = option.dataset.url;
    } else if (option.tagName === 'A') {
      option.click();
    }
  }

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (input.value) {
        event.stopPropagation();
        input.value = '';
        if (clearBtn) clearBtn.hidden = true;
        renderEmpty();
        input.focus();
      }
      return;
    }

    const options = optionList();
    if (event.key === 'ArrowDown') {
      if (!options.length) return;
      event.preventDefault();
      setActive(state.active + 1);
    } else if (event.key === 'ArrowUp') {
      if (!options.length) return;
      event.preventDefault();
      setActive(state.active - 1);
    } else if (event.key === 'Home' && options.length) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End' && options.length) {
      event.preventDefault();
      setActive(options.length - 1);
    } else if (event.key === 'Enter') {
      if (state.active >= 0 && options[state.active]) {
        event.preventDefault();
        activateOption(options[state.active]);
      }
    } else if (event.key === 'Tab') {
      setActive(-1);
    }
  });

  /* ------------------------------------------------------------------
     Input / form / panel events
     ------------------------------------------------------------------ */
  input.addEventListener('input', () => {
    if (clearBtn) clearBtn.hidden = !input.value;
    runSearchDebounced();
  });

  form.addEventListener('submit', () => {
    commitRecent(input.value.trim());
    track('akmas_search_submit', { term: input.value.trim() });
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      renderEmpty();
      input.focus();
    });
  }

  if (recentClear) {
    recentClear.addEventListener('click', () => {
      try {
        localStorage.removeItem(cfg.recentKey);
      } catch (error) {
        /* ignore */
      }
      renderRecent();
      setState(panel.dataset.state);
    });
  }

  if (seeAll) {
    seeAll.addEventListener('click', () => {
      commitRecent(state.q);
      track('akmas_search_result_clicked', { term: state.q, type: 'see_all', id: null, handle: null });
    });
  }

  panel.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-ak-add]');
    if (addButton) {
      track('akmas_search_add_to_cart', {
        term: state.q,
        variantId: addButton.getAttribute('data-ak-add'),
      });
      return;
    }

    const termButton = event.target.closest('[data-ak-term]');
    if (termButton) {
      const term = termButton.getAttribute('data-ak-term') || '';
      input.value = term;
      if (clearBtn) clearBtn.hidden = false;
      commitRecent(term);
      state.q = '';
      void runSearch();
      input.focus();
      return;
    }

    const row = event.target.closest('[role="option"]');
    if (row) {
      commitRecent(state.q);
      track('akmas_search_result_clicked', {
        term: state.q,
        type: row.dataset.type || null,
        id: row.dataset.id || null,
        handle: row.dataset.handle || null,
      });
      if (row.tagName !== 'A' && row.dataset.url) {
        window.location.href = row.dataset.url;
      }
    }
  });

  /* ------------------------------------------------------------------
     Dialog open/close + viewport positioning
     ------------------------------------------------------------------ */
  function updatePosition() {
    const header =
      document.getElementById('shopify-section-akmas-header') ||
      document.querySelector('.shopify-section[id*="akmas-header"]') ||
      document.querySelector('header') ||
      document.querySelector('.ak-header');
    const viewport = window.visualViewport;
    /* On mobile the keyboard shrinks visualViewport and can scroll it down.
       Subtracting offsetTop keeps the panel anchored just under the header
       while its bottom edge stays above the keyboard. */
    const offsetTop = viewport ? Math.max(0, Math.round(viewport.offsetTop)) : 0;
    const headerBottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    const top = Math.max(0, headerBottom - offsetTop);
    rootEl.style.setProperty('--ak-search-top', `${top}px`);
    const height = viewport ? viewport.height : window.innerHeight;
    rootEl.style.setProperty('--ak-search-vh', `${Math.round(height)}px`);
  }

  function onDialogOpen() {
    updatePosition();
    if (clearBtn) clearBtn.hidden = !input.value;
    if (norm(input.value).length >= 2) void runSearch();
    else renderEmpty();
    ensureIndex();
    input.focus({ preventScroll: true });
    if (input.value) input.select();
    track('akmas_search_opened', { term: input.value.trim() });
  }

  rootEl.addEventListener('dialog:open', onDialogOpen);
  if (dialogEl.open) onDialogOpen();

  const onViewportChange = () => {
    if (dialogEl.open) updatePosition();
  };
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onViewportChange);
    window.visualViewport.addEventListener('scroll', onViewportChange);
  }

  renderEmpty();
}
